import { useState, useRef, useCallback } from 'react';
import { Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NsiteBookmark } from '@/desktop/apps/NsiteBrowserApp';
import { useDesktop } from '@/desktop/DesktopContext';

interface DesktopShortcutProps {
  shortcut: NsiteBookmark;
  /** Initial position on the desktop */
  initialX?: number;
  initialY?: number;
}

export function DesktopShortcut({ shortcut, initialX = 24, initialY = 24 }: DesktopShortcutProps) {
  const { openNsite } = useDesktop();
  const [pos, setPos] = useState({ x: initialX, y: initialY });
  const [selected, setSelected] = useState(false);
  const [faviconError, setFaviconError] = useState(false);

  // Drag state
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    dragging.current = true;
    hasDragged.current = false;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setSelected(true);

    const onMouseMove = (me: MouseEvent) => {
      if (!dragging.current) return;
      const newX = me.clientX - dragOffset.current.x;
      const newY = me.clientY - dragOffset.current.y;
      if (Math.abs(newX - pos.x) > 4 || Math.abs(newY - pos.y) > 4) {
        hasDragged.current = true;
      }
      setPos({ x: newX, y: newY });
    };

    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [pos]);

  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasDragged.current) {
      openNsite(shortcut.url);
    }
    setSelected(false);
  }, [openNsite, shortcut.url]);

  // Clamp title to 2 lines worth of characters
  const displayTitle = shortcut.title.length > 28
    ? shortcut.title.slice(0, 26) + '…'
    : shortcut.title;

  return (
    <div
      className="absolute flex flex-col items-center gap-1.5 cursor-pointer select-none group"
      style={{ left: pos.x, top: pos.y, width: 88, zIndex: 50 }}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      title={`${shortcut.title}\n${shortcut.url}\nDouble-click to open`}
    >
      {/* Icon — no background box, just the image/globe floating on the desktop */}
      <div
        className={cn(
          'w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-150',
          selected
            ? 'ring-2 ring-white/80 ring-offset-2 ring-offset-transparent brightness-110'
            : 'group-hover:scale-110 group-hover:drop-shadow-lg'
        )}
      >
        {shortcut.favicon && !faviconError ? (
          <img
            src={shortcut.favicon}
            alt={shortcut.title}
            className="w-16 h-16 rounded-2xl object-cover shadow-lg"
            onError={() => setFaviconError(true)}
            draggable={false}
          />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-slate-700/80 flex items-center justify-center shadow-lg">
            <Globe className="w-9 h-9 text-white/70" />
          </div>
        )}
      </div>

      {/* Label */}
      <span
        className={cn(
          'text-white text-[11px] font-medium text-center leading-tight px-1.5 py-0.5 rounded w-full',
          'break-words line-clamp-2',
          selected ? 'bg-primary/70' : 'bg-black/50'
        )}
        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)', backdropFilter: 'blur(4px)' }}
      >
        {displayTitle}
      </span>
    </div>
  );
}
